import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import * as yup from 'yup';

import {
  Box,
  Button,
  Flex,
  TextInputField,
  InternationalPhoneInputField,
  type InternationalPhoneData,
  spaces,
} from '@electricjs/arc';

import { useGlobalUI } from '@/components/GlobalUIProvider';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import {
  type Address,
  AddressGroupField,
} from '@/components/AddressGroupField';
import { useCreateSiteMutation } from '@/redux/slices/assetApiSlice';
import { internationalPhoneValidationSchema } from '@common';

const GOOGLE_MAPS_API_KEY = window._env_?.VITE_GOOGLE_MAPS_PUB_API_KEY;

export type FormData = {
  name: string;
  phone?: InternationalPhoneData | null;
  address: Address;
};

export const zipCodeCountriesList = ['ca', 'us', 'pr'];

export const addressSiteValidationSchema = yup.lazy(address => {
  const shouldRequireZipCode = zipCodeCountriesList.includes(
    address?.country?.toLowerCase()
  );

  return yup.object<Address>().shape({
    streetAddress1: yup.string().required('street'),
    streetAddress2: yup.string().nullable(),
    city: yup.string().required('city'),
    state: yup.string().required('state'),
    country: yup.string().required('country'),
    zip: shouldRequireZipCode
      ? yup.string().required('zip code')
      : yup.string().optional(),
  });
});

export const getNewSiteSchema = (t: TFunction) =>
  yup.object().shape({
    name: yup.string().required(t('newSiteForm.nameRequired')),
    phone: yup
      .object()
      .shape({
        phoneNumber: internationalPhoneValidationSchema({ required: false }),
        country: yup.string(),
      })
      .nullable()
      .optional(),
    address: addressSiteValidationSchema,
  });

type NewSiteFormProps = {
  onSuccess?: () => void;
};

const NewSiteForm = ({ onSuccess }: NewSiteFormProps) => {
  const { t } = useTranslation('assets');
  const { showErrorToast, showSuccessToast } = useGlobalUI();

  const defaultValues: FormData = {
    name: '',
    phone: null,
    address: {
      streetAddress1: '',
      streetAddress2: '',
      city: '',
      state: '',
      zip: '',
      country: '',
    },
  };

  const {
    control,
    reset,
    handleSubmit,
    formState: { isValid },
    setValue,
  } = useForm<FormData>({
    resolver: yupResolver(getNewSiteSchema(t)),
    mode: 'onTouched',
    defaultValues,
  });

  const [createSite, { isLoading }] = useCreateSiteMutation();

  const organizationId = useGetOrganizationId();

  const submitSiteCreation = (data: FormData) => {
    // Format phone number with country code if provided
    const formattedPhone = data.phone?.phoneNumber
      ? `${data.phone.phoneNumber}`
      : undefined;

    createSite({
      name: data.name,
      organizationId: organizationId,
      address: {
        streetAddress1: data.address.streetAddress1,
        streetAddress2: data.address.streetAddress2,
        city: data.address.city,
        state: data.address.state,
        zip: data.address.zip,
        country: data.address.country,
      },
      phone: formattedPhone,
    })
      .unwrap()
      .catch(error => {
        console.error('Error while adding site: ', error);
        showErrorToast({
          id: 'add-site-error-toast',
          message: t('newSiteForm.errorMessage'),
          stack: true,
        });
        return Promise.reject(error);
      })
      .then(() => {
        showSuccessToast({
          id: 'add-site-success-toast',
          message: t('newSiteForm.successMessage'),
        });
        reset();
        onSuccess?.();
      });
  };

  const onAddSiteClick = (data: FormData) => {
    submitSiteCreation(data);
  };

  return (
    <Box>
      <form>
        <Flex vertical mb="2rem" width="100%">
          <Flex width="100%" columnGap="1rem">
            <TextInputField
              id="site-name-input"
              name="name"
              control={control}
              label={t('newSiteForm.name')}
              placeholder={t('newSiteForm.namePlaceholder')}
              width="100%"
              required
            />
            <Box width="100%">
              <InternationalPhoneInputField
                id="site-phone-number-input"
                name="phone"
                control={control}
                label={t('newSiteForm.phone')}
                placeholder={t('newSiteForm.phonePlaceholder')}
                formatAsYouType={true}
                variant="compact"
              />
            </Box>
          </Flex>
          <Flex width="100%" alignItems="flex-start" columnGap="1rem">
            <AddressGroupField
              id="site-address-search"
              name="address"
              control={control}
              label={t('sites.address')}
              onChange={address =>
                setValue('address', address, { shouldValidate: true })
              }
              googleMapsApiKey={GOOGLE_MAPS_API_KEY}
              required
            />
            <Button
              id="add-site-button"
              flexBasis="10rem"
              mt={spaces.space_l_rem}
              disabled={!isValid}
              loading={isLoading}
              onClick={handleSubmit(onAddSiteClick)}>
              {t('newSiteForm.addSiteButton')}
            </Button>
          </Flex>
        </Flex>
      </form>
    </Box>
  );
};

export default NewSiteForm;
